/**
 * The Quest's reveals (quest-journey-2026-10-27).
 *
 * A constellation plays where the person earned it, over the screen they are
 * on, at the natural end of the act: after a reading closes, never
 * mid-reading or mid-interview. Earned while away, it plays on the next app
 * open, wherever they land. Each reveal runs the same way (DESIGN "Each
 * reveal"): the section's own animation; a light flies to the bar, which
 * makes room first, then the tab is born or changes its face as the light
 * lands; then a hero whose "Get started" lands INSIDE the new room.
 *
 * The server says which are due (`nav.reveals`, oldest first) and forgets one
 * on `nav/seen/ reveal:<key>`.
 */
import { sectionKey } from '@/utils/quest-sky'

/**
 * Per constellation: the bar slot it lands on and how (born, a face change,
 * or a ring on a tab already there), and the room "Get started" opens.
 */
export const REVEALS = {
  fs: { to: 'journal', change: 'born' },
  ri: { to: 'mirror', from: 'journal', change: 'upgr' },
  ve: { to: 'mirror', change: 'ring' },
  zw: { to: 'community', from: 'mystic', change: 'upgr' },
  mo: { to: 'altar', from: 'mirror', change: 'upgr' },
  su: { to: 'studio', change: 'born' },
}

// The built path (an older server): the room each constellation opened.
const ROOM_OF = { fs: 'journal', ri: 'mirror', ve: 'circle', zw: 'community', mo: 'altar', su: 'studio' }
export const roomOf = (key) => ROOM_OF[key] || ''

/**
 * Revision 5 (recipient polish 2026-11-03, DESIGN §12, §14; BE-CONTRACT §2):
 * the server names the room a reveal opens (`reveal`), and the slot change
 * follows the room. The Altar arrives with The Rising (Journal → Altar); the
 * Circle, a room inside the Altar, rings it; the Studio is born.
 */
const BY_ROOM = {
  journal: { to: 'journal', change: 'born' },
  altar: { to: 'altar', from: 'journal', change: 'upgr' },
  mirror: { to: 'mirror', from: 'journal', change: 'upgr' },
  circle: { to: 'altar', change: 'ring' },
  community: { to: 'community', from: 'mystic', change: 'upgr' },
  studio: { to: 'studio', change: 'born' },
}
// A room's constellation, for the art and the eyebrow when only a hero is owed.
const KEY_OF_ROOM = { journal: 'fs', altar: 'ri', mirror: 'ri', circle: 've', community: 'zw', studio: 'su' }
/** The second light of a double arrival follows the first this soon (ms), with no second art. */
export const CHAIN_MS = 250

/**
 * The due reveals (`nav.reveals`, else `nav.unlock`, API §1.2/§2) as
 * [{ key: 'ri', seen: 'unlock:the_rising', room: 'altar', hero: 'hero:altar', plays, saved, … }].
 * A ring (the Circle, inside a tab already there) plays after the others, so
 * the Community's light lands first and the Altar rings straight after.
 */
export function readReveals (nav) {
  if (!nav) return []
  const list = Array.isArray(nav.reveals) && nav.reveals.length ? nav.reveals : (nav.unlock ? [nav.unlock] : [])
  const out = []
  for (const r of list) {
    const raw = typeof r === 'string' ? { key: r } : (r && typeof r === 'object' ? r : null)
    if (!raw) continue
    const key = sectionKey(raw.constellation || String(raw.key || '').replace(/^unlock:/, ''))
    if (!key || !REVEALS[key]) continue
    const apiKey = raw.constellation || String(raw.key || '').replace(/^unlock:/, '')
    const room = raw.reveal || ROOM_OF[key]
    const seen = /^unlock:/.test(String(raw.key || '')) ? String(raw.key) : `unlock:${apiKey}`
    if (out.some((x) => x.seen === seen)) continue
    out.push({ ...raw, key, apiKey, room, seen, hero: `hero:${room}`, heroNow: false, plays: raw.plays || (key === 'su' ? 'quest_or_altar' : 'where_you_are') })
  }
  const ring = (x) => (x.room === 'circle' ? 1 : 0)
  return out.map((x, i) => [x, i]).sort((a, b) => ring(a[0]) - ring(b[0]) || a[1] - b[1]).map((p) => p[0])
}

/**
 * A hero still owed with no reveal to play first (`nav.heroes_due`). It opens
 * the first time they are inside that room (DESIGN §8a), never elsewhere;
 * `here` is the room they are in. Without `here`, the first one owed.
 */
export function heroDue (nav, here) {
  const rooms = nav && Array.isArray(nav.heroes_due) ? nav.heroes_due : (nav && Array.isArray(nav.heroesDue) ? nav.heroesDue : [])
  for (const room of rooms) {
    const key = KEY_OF_ROOM[room]
    if (!key) continue
    // The Circle lives inside the Mirror: its hero opens there too.
    if (here !== undefined && room !== here && !(room === 'circle' && here === 'mirror')) continue
    return { key, apiKey: room, room, heroOnly: true, hero: `hero:${room}`, tour: room === 'studio' }
  }
  return null
}

// The bar tabs a hero can glow on (a tab born or changing face); never Quest or the Mystic.
const GLOW_ROOM = { journal: 'journal', altar: 'altar', mirror: 'mirror', community: 'community', studio: 'studio' }
/**
 * The reveal-or-dot rule (DESIGN §14): a new tab glows softly until first
 * opened (its hero owed, and they are not in it): no dot, no number. A new
 * room inside a tab is the server's quiet dot, never on a glowing tab.
 */
export function glowFor (items, nav, { here = '' } = {}) {
  if (!Array.isArray(items)) return items
  const owed = (nav && (nav.heroesDue || nav.heroes_due)) || []
  return items.map((i) => {
    const room = GLOW_ROOM[i.key]
    const glow = !!room && owed.includes(room) && here !== room
    return { ...i, glow, dot: glow ? false : !!i.dot }
  })
}

/** The one to play now: the oldest not yet played this visit. */
export function dueReveal (reveals, played = new Set()) {
  return (reveals || []).find((r) => !played.has(r.key)) || null
}

/** At the natural end of an act only. */
export function revealMayPlay ({ signedIn = false, inReading = false, interviewing = false, introRoom = false, busy = false } = {}) {
  return !!signedIn && !inReading && !interviewing && !introRoom && !busy
}

/**
 * The slot change for a reveal: the server's `unlock.bar` ({slot, to, from})
 * when it sends one (rooms open in any order since 2026-10-30, so the Journal
 * can land under an open Mirror: a ring), else the built-in path. `from` null:
 * the tab is born; `from === to`: a ring; else the slot changes its face.
 */
export function revealSpec (reveal) {
  if (!reveal) return null
  const spec = (reveal.reveal && BY_ROOM[reveal.room]) || REVEALS[reveal.key]
  const b = reveal.bar
  if (!spec || !b || typeof b !== 'object' || !b.to || !(b.to in FACES)) return spec
  if (b.from && b.from !== 'mystic' && !(b.from in FACES)) return spec
  if (!b.from) return { to: b.to, change: 'born' }
  if (b.from === b.to) return { to: b.to, change: 'ring' }
  return { to: b.to, from: b.from, change: 'upgr' }
}

// The face a slot shows before the light lands.
const FACES = {
  journal: { label: 'Journal', icon: 'fa-book-open' },
  mirror: { label: 'Mirror', icon: '' },
  altar: { label: 'Altar', icon: 'fa-eye' },
  community: { label: 'Community', icon: 'fa-comments' },
  studio: { label: 'Studio', icon: 'fa-wand-magic-sparkles' },
}

/**
 * The bar during a reveal. `items` is the bar the server sends now (with the
 * new tab); phase 'before' is the bar as it was, 'room' makes room for a tab
 * still to be born, 'arrived' is the new bar with the arrival animated.
 */
export function revealBar (items, reveal, phase, { mystic = null } = {}) {
  if (!reveal || !phase || !Array.isArray(items)) return items
  const spec = revealSpec(reveal)
  if (!spec) return items
  const at = items.findIndex((i) => i.key === spec.to)
  if (at === -1) return items
  const out = items.map((i) => ({ ...i }))
  if (spec.change === 'born') {
    if (phase === 'before') return out.filter((_, i) => i !== at)
    if (phase === 'room') out[at].pending = true
    else out[at].anim = 'born'
    out[at].slot = spec.to
    return out
  }
  if (spec.change === 'ring') {
    if (phase === 'arrived') out[at].anim = 'ring'
    out[at].slot = spec.to
    return out
  }
  if (phase === 'arrived') { out[at].anim = 'upgr'; out[at].slot = spec.to; return out }
  // The old face, still in the slot the light will land on.
  const old = spec.from
  const face = old === 'mystic'
    ? { key: 'mystic', label: (mystic && mystic.label) || 'Mystic AIda', icon: '', portrait: (mystic && mystic.portrait) || '', sub: (mystic && mystic.sub) || 'iam' }
    : { key: old, label: FACES[old].label, icon: FACES[old].icon, portrait: '' }
  out[at] = { ...out[at], ...face, dot: false, slot: spec.to }
  return out
}

/** Where "Get started" lands: inside the new room, never the Quest. */
export function landingFor (reveal) {
  const room = reveal && (reveal.room || ROOM_OF[reveal.key])
  switch (room) {
    case 'journal': {
      const r = reveal.reading
      const id = r && typeof r === 'object' ? r.id : r
      return id ? { type: 'journal', reading: String(id), reader: (r && r.reader) || '' } : { type: 'journal' }
    }
    case 'altar': return { type: 'altar-ritual' }
    case 'mirror': return { type: 'mirror' }
    case 'circle': return { type: 'circle' }
    case 'community': return { type: 'community' }
    case 'studio': return { type: 'studio-entry' }
  }
  switch (reveal && reveal.key) {
    case 'fs': {
      const r = reveal.reading
      const id = r && typeof r === 'object' ? r.id : r
      return id ? { type: 'journal', reading: String(id), reader: (r && r.reader) || '' } : { type: 'journal' }
    }
    case 'ri': return { type: 'mirror' }
    case 've': return { type: 'circle' }
    case 'zw': return { type: 'community' }
    case 'mo': return { type: 'altar-ritual' }
    case 'su': return { type: 'studio-entry' }
    default: return null
  }
}

function andList (names) {
  const n = (names || []).filter(Boolean)
  if (n.length <= 1) return n.join('')
  return `${n.slice(0, -1).join(', ')} and ${n[n.length - 1]}`
}

/**
 * The hero over the new room (prototype HERO), from what the reveal carries.
 * Bodies may bold with **…**; the app renders them as segments.
 */
export function heroFor (reveal) {
  const r = reveal || {}
  // The Altar arrives with The Rising (revision 5): the built Altar hero.
  if (r.room === 'altar' && r.key !== 'mo') {
    return { ...heroFor({ ...r, key: 'mo', room: '' }), eyebrow: 'The Rising' }
  }
  const byRoom = r.room && KEY_OF_ROOM[r.room]
  switch (byRoom || r.key) {
    case 'fs': return {
      eyebrow: 'First Star', title: 'Your Journal', art: 'fs',
      body: ['Every reading you keep lives here, with what landed and how it felt.', 'Come back to any of them, whenever you like.'],
    }
    case 'ri': {
      const sg = r.signs || {}
      const parts = [sg.sun && `Sun in ${sg.sun}`, sg.moon && `Moon in ${sg.moon}`, sg.rising && `${sg.rising} rising`].filter(Boolean)
      const chart = parts.length ? `: **${parts.join(', ')}**` : ''
      return {
        eyebrow: 'The Rising', title: r.name || 'Your Mirror', art: 'portrait', portrait: r.portrait || '',
        body: [`This is your Mirror. Your sky gave you this name, and a portrait painted from your chart${chart}.`,
          'Your first conjured look is waiting in Looks. Change any of it, any time.'],
      }
    }
    case 've': {
      const from = r.from || (r.gift && r.gift.from) || ''
      const here = from ? `**${from} is already here.** ${from} read for you first.`
        : (r.people && r.people.length ? `${andList(r.people)} ${r.people.length === 1 ? 'is' : 'are'} here.` : '')
      return {
        eyebrow: 'Venus', title: 'Your Circle', art: 've',
        body: [`The people you read for, and the people who read for you.${here ? ' ' + here : ''}`,
          'When someone writes, you’ll find it under Messages, in your menu.'],
        quiet: 'In time, you can ask up to 13 of them into your coven, the few who see your real readings. Each one chooses.',
      }
    }
    case 'zw': return {
      eyebrow: 'Zodiac Wheel', title: 'The Community', art: 'zw',
      body: [`**Lessons**: what readings taught other seekers, in their own words.${r.lesson_from ? ` ${r.lesson_from}’s is saved for you.` : ''}`,
        `**Mystics**: every voice that reads here. ${(r.mystic && r.mystic.label) || 'Mystic AIda'} lives here now.`,
        '**Voice**: where everyone helps choose what comes next.'],
    }
    case 'mo': return {
      eyebrow: 'The Moon', title: 'Your Altar', art: 'mo',
      body: ['Your first horoscope is here, read from your own chart. A new one arrives each morning.',
        'Your Mirror and Journal live here now too, beside it.'],
    }
    case 'su': {
      const m = r.mystic && r.mystic.name
      return {
        eyebrow: 'The Sun', title: 'Your Studio', art: 'square', portrait: (r.mystic && r.mystic.portrait) || '',
        body: [m ? `**${m}** was drawn from your chart, the way your own name was.` : 'Your own Mystic is waiting to be made, drawn from your chart.',
          'Tune its voice, choose how it reads, then share it with the people you read for.'],
      }
    }
    default: return null
  }
}

// When the light leaves the art (s) and how long reduced motion holds (s): the prototype's T.
const T = { fs: [2.7, 1.6], ri: [5.6, 2.0], ve: [4.6, 1.8], zw: [5.0, 1.8], mo: [5.2, 2.0], su: [5.6, 2.0] }
/** ms: when the light leaves, when the art fades, when the hero rises. */
export function revealTiming (key, reduced = false) {
  const t = T[key] || [3, 1.6]
  const light = Math.round((reduced ? t[1] : t[0]) * 1000)
  const fade = light + (reduced ? 700 : 1500)
  return { light, fade, hero: fade + (reduced ? 200 : 350) }
}

/**
 * The folded bar ("Menu", in the gift room or a reading) opens for a beat as
 * a tab is born or changes face, so the light has a tab to land on, then
 * folds again (DESIGN §5 "the Menu opening for a beat as Journal is born";
 * prototype sealAndBirth). Walk 2026-11-03 A: folded, the light found no tab
 * and the Journal never showed. 'open' | 'fold' | ''.
 */
export const BAR_BEAT_MS = 2000
export function barBeat (phase, { collapsed = false, opened = false } = {}) {
  if (phase === 'room' && collapsed) return 'open'
  if (phase === 'arrived' && opened) return 'fold'
  return ''
}
